import { useEffect, useState } from "react";

import { FilePreview } from "@/components/FilePreview";
import { OcrHighlightRequest } from "@/components/FilePreview/types";
import { stripBundlePrefix } from "@/utils/bundle-name";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import { fetchFileSasUrl } from "@hooks/queries/use-files";

type PreviewFile = {
  id: string;
  name: string;
};

type DocumentPreviewDialogProps = {
  /** The file to preview. When present, the dialog is open. */
  file: PreviewFile;
  /** 1-based page to open on (e.g. a semantic-search citation). */
  page?: number;
  /** Case number — needed to fetch OCR lines for source highlighting. */
  caseNumber?: string;
  /** Cited passages to highlight across the document's pages (MDRS-134). */
  highlights?: OcrHighlightRequest[];
  /** Called when the dialog is dismissed. */
  onClose: () => void;
};

type FilePreviewType = "pdf" | "image" | "docx" | "other";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

const resolveFileType = (name: string): FilePreviewType => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (ext === "docx" || ext === "doc") return "docx";
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  return "other";
};

/**
 * Document preview for the semantic-search source citations (MDRS-122).
 *
 * Wraps the shared {@link FilePreview}, which brings zoom in/out controls and a
 * ResizeObserver-driven responsive reflow, and jumps to the cited `page` on
 * open. Replaces the older, fixed-size `PdfViewerDialog` that had neither zoom
 * nor resize.
 */
const DocumentPreviewDialog = ({
  file,
  page = 1,
  caseNumber,
  highlights,
  onClose,
}: DocumentPreviewDialogProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fileType = resolveFileType(file.name);
  const displayName = stripBundlePrefix(file.name);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setHasError(false);
    setFileUrl(null);

    // Resolve a read-only, file-scoped SAS URL and hand it straight to
    // FilePreview. Passing the signed URL directly matches the previous
    // working behaviour and avoids tripping the storage account's CORS policy
    // on local dev (which a fetch+blob round-trip would).
    fetchFileSasUrl(file.id)
      .then(({ url }) => {
        if (cancelled) return;
        setFileUrl(url);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        console.error("Error fetching document preview URL:", error);
        setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file.id]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[90vh] w-[60vw] max-w-4xl flex-col sm:max-w-[60vw]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle title={displayName} className="truncate pr-8 text-left">
            {displayName}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1">
          <FilePreview
            key={fileUrl ?? "loading"}
            fileUrl={fileUrl}
            fileType={fileType}
            filename={file.name}
            isLoading={isLoading}
            fileLoadError={hasError}
            className="h-full"
            showZoomControls
            initialPage={page}
            ocrHighlight={
              caseNumber && highlights && highlights.length > 0
                ? {
                    caseNumber,
                    fileName: file.name,
                    highlights,
                  }
                : undefined
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
